/* eslint-disable max-len */
import type { ReducerState } from "react";
import type React from "react";

import type { ESerpTypes } from "app/common/serp/SerpFilterMaps";
import type { IEditableSearchEngine } from "app/components/campaigns/searchEngine/SearchEnginesConsts";
import { translate } from "app/filters/i18nFilter";
import type { editCampaignSettingsDataReducer } from "app/pages/digital-marketing/keywords-campaigns/edit-campaign/EditCampaignSettingsDataReducer";
import type { EditedTags } from "app/pages/digital-marketing/keywords-campaigns/shared/components/EditTagsModal/types";
import type { IKeywordsCampaignKeywordsTableData } from "app/pages/digital-marketing/keywords-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/types";
import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";

import type { IKeywordAndTags, ITargetUrlIconProps } from "../infra/KeywordsCampaignTypes";

export const EditSectionsConfig = {
    editGeneralSettings: {
        get title() {
            return translate("campaign.settings.section.general");
        },
        id: "editGeneralSettings",
    },
    editCompetitors: {
        get title() {
            return translate("campaign.settings.section.competitors");
        },
        id: "editCompetitors",
    },
    editSearchEngines: {
        get title() {
            return translate("campaign.settings.section.search.engine");
        },
        id: "editSearchEngines",
    },
    editKeywords: {
        get title() {
            return translate("common.keywords");
        },
        id: "editKeywords",
    },
    editPermissions: {
        get title() {
            return translate("rank.tracker.settings.sharing.invite.section.title");
        },
        id: "editPermissions",
    },
};

export enum EAssetRole {
    Viewer,
    Editor,
    Owner,
}

export interface AssetUser {
    userId: number;
    assetRole: EAssetRole;
    text?: string;
    email?: string;
}

export const ERROR_SAVE_CAMPAIGN_NAME = "campaign.settings.save.changes.tooltip";
export const ERROR_SAVE_CAMPAIGN_SEARCH_ENGINES = "campaign.settings.save.searchEngineError.tooltip";
export const ERROR_SAVE_EXCLUDED_FEATURES = "campaign.settings.save.excludedFeaturesError.tooltip";

export interface EditCampaignSettingsState {
    competitorsList: IAsset[];
    asset?: IAsset;
    campaignName: string;
    initialCampaignName: string;
    isCampaignNameValid: boolean;
    campaignNameErrorMessage?: string;
    isLoading: boolean;
    searchEngineItems: IEditableSearchEngine[];
    ref?: React.RefObject<HTMLElement>;
    keywordsAndTags: IKeywordsCampaignKeywordsTableData;
    remainingKeywordCount: number;
    isShared: boolean;
    entities: string[];
    unassignedScrapingConfs: string[];
    maxAllowedTrackedSearches: number;
    originalTrackedSearchesCountWithoutCurrentCampaign: number;
    sharedUsers?: AssetUser[];
    /**
     *  isDirty: set to true when the user changed keyword or Search Engine - only then we will check if we need to disable the save button
     */
    isDirty: boolean;
    isSubscribed: boolean;
    positionExcludedSerpFeatures: ESerpTypes[];
}

type EditCampaignSettingsStateReducerType = typeof editCampaignSettingsDataReducer;

export interface IEditCampaignPageTitleProps {
    isCampaignNameValid: boolean;
    isSticked: boolean;
    asset?: IAsset;
    campaignState: ReducerState<EditCampaignSettingsStateReducerType>;
    originalCampaignState: EditCampaignSettingsState;
    searchEngineItems: IEditableSearchEngine[];
    existsUnassignedScrapingConfs: boolean;
    remainingKeywordCount: number;
    isDirty: boolean;
}

export enum EditCampaignSettingsReducerTypes {
    InitialCampaignData = "InitialCampaignData",
    SetCompetitors = "SetCompetitors",
    SetSearchEngineItems = "SetSearchEngineItems",
    SetIsCampaignName = "SetIsCampaignName",
    SetIsLoading = "SetIsLoading",
    UpdateTableData = "UpdateTableData",
    SetEntities = "SetEntities",
    UpdateSearchEngine = "UpdateSearchEngine",
    UpdateTags = "UpdateTags",
    SetSharedUsers = "SetSharedUsers",
    UpdateTargetUrl = "UpdateTargetUrl",
    UpdateEmailSubscriptionsStatus = "UpdateEmailSubscriptionsStatus",
    SetPositionExcludedSerpFeatures = "SetPositionExcludedSerpFeatures",
}

export enum EChangeStateScrapingConf {
    ADD,
    REMOVE,
    DRAFT,
}

export type EditCampaignSettingsDataActions =
    | {
          type: EditCampaignSettingsReducerTypes.SetSharedUsers;
          payload: {
              sharedUsers: AssetUser[];
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.InitialCampaignData;
          payload: EditCampaignSettingsState;
      }
    | {
          type: EditCampaignSettingsReducerTypes.SetCompetitors;
          payload: {
              newCompetitorsList;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.SetSearchEngineItems;
          payload: {
              searchEngineItems: IEditableSearchEngine[];
              isApplyToAllKeywords?: boolean;
              stateChange: EChangeStateScrapingConf;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.SetIsCampaignName;
          payload: {
              isCampaignNameValid: boolean;
              campaignName: string;
              campaignNameErrorMessage: string;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.UpdateSearchEngine;
          payload: {
              newSelectedSearchEngines: string[];
              changedKeywords: string[];
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.UpdateTableData;
          payload: {
              updateTableData: IKeywordsCampaignKeywordsTableData;
              amountOfAddedTrackedSearches: number;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.UpdateTags;
          payload: {
              updateTableData: EditedTags[];
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.SetEntities;
          payload: {
              entities;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.UpdateTargetUrl;
          payload: {
              row: IKeywordAndTags;
              keywordToTargetUrls: string[];
              targetUrlIcon: ITargetUrlIconProps;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.UpdateEmailSubscriptionsStatus;
          payload: {
              isSubscribed: boolean;
          };
      }
    | {
          type: EditCampaignSettingsReducerTypes.SetPositionExcludedSerpFeatures;
          payload: {
              positionExcludedSerpFeatures: ESerpTypes[];
          };
      };

export interface IUpdateStateParamsForSearchEngineEvent {
    tableState: IKeywordsCampaignKeywordsTableData;
    remainingCount: number;
}

export interface IGetUnassignedScrapingConfsAfterSCUpdate {
    currentUnassignedScrapingConfs: string[];
    stateChange: EChangeStateScrapingConf;
    searchEngineItems: IEditableSearchEngine[];
    isApplyToAllKeywords: boolean;
}

export interface IStateTragetUrl {
    keywordToTargetUrls: string[];
    targetUrlIcon: ITargetUrlIconProps;
    state: EditCampaignSettingsState;
    row: IKeywordAndTags;
}
